import React, { useState } from 'react';
import {
  Users, Settings, BarChart3, RefreshCw,
  ChevronDown, ChevronUp, Check, X, AlertCircle,
  ArrowUpDown, Play, Pause, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../hooks/useToast';
import { Team, TeamMember, DistributionConfig, DistributionStats } from '../types/Team';
import api from '../services/api';

export default function LeadDistribution() {
  const navigate = useNavigate();
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null);
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Buscar equipes
  const { data: teams, isLoading: teamsLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await api.get<{ results: Team[] }>('/auth/teams/');
      return response.data.results || response.data;
    }
  });

  // Buscar configurações de distribuição da equipe selecionada
  const { data: distributionConfigs, isLoading: configsLoading } = useQuery({
    queryKey: ['distribution-configs', selectedTeamId],
    queryFn: async () => {
      if (!selectedTeamId) return [];
      const response = await api.get(`/auth/teams/${selectedTeamId}/distribution-configs/`);
      return response.data as DistributionConfig[];
    },
    enabled: !!selectedTeamId
  });

  // Buscar estatísticas de distribuição
  const { data: distributionStats } = useQuery({
    queryKey: ['distribution-stats', selectedTeamId],
    queryFn: async () => {
      if (!selectedTeamId) return null;
      const response = await api.get(`/auth/teams/${selectedTeamId}/distribution-stats/`);
      return response.data as DistributionStats;
    },
    enabled: !!selectedTeamId
  });

  // Buscar membros da equipe
  const { data: teamMembers } = useQuery({
    queryKey: ['team-members', selectedTeamId],
    queryFn: async () => {
      if (!selectedTeamId) return [];
      const response = await api.get(`/auth/teams/${selectedTeamId}/members/`);
      return response.data as TeamMember[];
    },
    enabled: !!selectedTeamId
  });

  // Configurar distribuição padrão
  const setupDistributionMutation = useMutation({
    mutationFn: async ({ teamId, distributionType }: { teamId: number; distributionType: string }) => {
      return await api.post(`/auth/teams/${teamId}/setup-distribution/`, {
        distribution_type: distributionType
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-configs'] });
      queryClient.invalidateQueries({ queryKey: ['distribution-stats'] });
      showToast('Distribuição configurada com sucesso!', 'success');
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Erro ao configurar distribuição', 'error');
    }
  });

  // Atualizar configuração
  const updateConfigMutation = useMutation({
    mutationFn: async ({ configId, data }: { configId: number; data: Partial<DistributionConfig> }) => {
      return await api.patch(`/auth/distribution-configs/${configId}/`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-configs'] });
      showToast('Configuração atualizada!', 'success');
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Erro ao atualizar', 'error');
    }
  });

  // Toggle disponibilidade do membro
  const toggleMemberAvailabilityMutation = useMutation({
    mutationFn: async ({ teamId, memberId, isAvailable }: { teamId: number; memberId: number; isAvailable: boolean }) => {
      return await api.patch(`/auth/teams/${teamId}/toggle-member-availability/`, {
        member_id: memberId,
        is_available: isAvailable
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      showToast('Disponibilidade atualizada!', 'success');
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Erro ao atualizar disponibilidade', 'error');
    }
  });

  // Atualizar ordem dos membros
  const updateMemberOrderMutation = useMutation({
    mutationFn: async ({ teamId, orders }: { teamId: number; orders: { member_id: number; order: number }[] }) => {
      return await api.patch(`/auth/teams/${teamId}/update-member-order/`, { orders });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      showToast('Ordem atualizada!', 'success');
    },
    onError: (error: any) => {
      showToast(error.response?.data?.error || 'Erro ao atualizar ordem', 'error');
    }
  });

  const moveMember = (memberId: number, direction: 'up' | 'down') => {
    if (!teamMembers || !selectedTeamId) return;

    const sortedMembers = [...teamMembers].sort((a, b) => a.distribution_order - b.distribution_order);
    const currentIndex = sortedMembers.findIndex(m => m.id === memberId);

    if (direction === 'up' && currentIndex > 0) {
      const newOrders = sortedMembers.map((m, i) => {
        if (i === currentIndex) return { member_id: m.id, order: currentIndex - 1 };
        if (i === currentIndex - 1) return { member_id: m.id, order: currentIndex };
        return { member_id: m.id, order: i };
      });
      updateMemberOrderMutation.mutate({ teamId: selectedTeamId, orders: newOrders });
    } else if (direction === 'down' && currentIndex < sortedMembers.length - 1) {
      const newOrders = sortedMembers.map((m, i) => {
        if (i === currentIndex) return { member_id: m.id, order: currentIndex + 1 };
        if (i === currentIndex + 1) return { member_id: m.id, order: currentIndex };
        return { member_id: m.id, order: i };
      });
      updateMemberOrderMutation.mutate({ teamId: selectedTeamId, orders: newOrders });
    }
  };

  const selectedTeam = teams?.find(t => t.id === selectedTeamId);

  if (teamsLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid var(--glass-border)',
          borderTopColor: 'var(--color-primary-600)',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Back to Settings */}
      <button
        onClick={() => navigate('/settings')}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
          padding: 'var(--space-1) 0',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary-600)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
      >
        <ArrowLeft size={16} />
        Voltar para Configurações
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>
            Distribuicao de Leads
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Configure como leads, conversas e negocios sao distribuidos entre os membros da equipe
          </p>
        </div>
      </div>

      {/* Seletor de Equipe */}
      <div style={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-glass)'
      }}>
        <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: '500', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
          Selecione uma Equipe
        </label>
        <select
          value={selectedTeamId || ''}
          onChange={(e) => setSelectedTeamId(e.target.value ? parseInt(e.target.value) : null)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--glass-border)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: 'var(--text-base)'
          }}
        >
          <option value="">Selecione...</option>
          {teams?.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name} ({team.member_count} membros)
            </option>
          ))}
        </select>
      </div>

      {selectedTeamId && (
        <>
          {/* Estatísticas */}
          {distributionStats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 'var(--space-4)'
            }}>
              <div style={{
                background: 'var(--glass-bg-strong)',
                backdropFilter: 'var(--glass-blur)',
                borderRadius: 'var(--radius-xl)',
                border: '1px solid var(--glass-border)',
                padding: 'var(--space-4)',
                boxShadow: 'var(--shadow-glass)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--color-primary-100), var(--color-primary-200))',
                    borderRadius: 'var(--radius-lg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BarChart3 style={{ width: '20px', height: '20px', color: 'var(--color-primary-600)' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                      {distributionStats.distributions_today}
                    </p>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                      Distribuicoes Hoje
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'var(--glass-bg-strong)',
                backdropFilter: 'var(--glass-blur)',
                borderRadius: 'var(--radius-xl)',
                border: '1px solid var(--glass-border)',
                padding: 'var(--space-4)',
                boxShadow: 'var(--shadow-glass)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--color-success-100), var(--color-success-200))',
                    borderRadius: 'var(--radius-lg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <RefreshCw style={{ width: '20px', height: '20px', color: 'var(--color-success-600)' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                      {distributionStats.distributions_week}
                    </p>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                      Esta Semana
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'var(--glass-bg-strong)',
                backdropFilter: 'var(--glass-blur)',
                borderRadius: 'var(--radius-xl)',
                border: '1px solid var(--glass-border)',
                padding: 'var(--space-4)',
                boxShadow: 'var(--shadow-glass)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, var(--color-info-100), var(--color-info-200))',
                    borderRadius: 'var(--radius-lg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Users style={{ width: '20px', height: '20px', color: 'var(--color-info-600)' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                      {teamMembers?.filter(m => m.is_available_for_distribution).length || 0}
                    </p>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                      Membros Disponiveis
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Configurações de Distribuição */}
          <div style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-6)',
            boxShadow: 'var(--shadow-glass)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
              <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: '600', color: 'var(--text-primary)' }}>
                Configuracoes de Distribuicao
              </h2>
              {(!distributionConfigs || distributionConfigs.length === 0) && (
                <button
                  onClick={() => setupDistributionMutation.mutate({ teamId: selectedTeamId, distributionType: 'round_robin' })}
                  disabled={setupDistributionMutation.isPending}
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
                    color: 'white',
                    padding: 'var(--space-2) var(--space-4)',
                    borderRadius: 'var(--radius-lg)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    fontWeight: '500'
                  }}
                >
                  <Settings style={{ width: '16px', height: '16px' }} />
                  {setupDistributionMutation.isPending ? 'Configurando...' : 'Ativar Round Robin'}
                </button>
              )}
            </div>

            {configsLoading ? (
              <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
            ) : distributionConfigs && distributionConfigs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {distributionConfigs.map((config) => (
                  <div
                    key={config.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 'var(--space-4)',
                      background: 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid var(--glass-border)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: config.is_active ? 'var(--color-success-500)' : 'var(--color-gray-400)'
                      }} />
                      <div>
                        <p style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                          {config.entity_type_display}
                        </p>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                          {config.distribution_type_display}
                          {config.max_open_items > 0 && ` (max: ${config.max_open_items})`}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <select
                        value={config.distribution_type}
                        onChange={(e) => updateConfigMutation.mutate({
                          configId: config.id,
                          data: { distribution_type: e.target.value as any }
                        })}
                        style={{
                          padding: 'var(--space-2)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--glass-border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                          fontSize: 'var(--text-sm)'
                        }}
                      >
                        <option value="round_robin">Round Robin</option>
                        <option value="load_balance">Balanceamento</option>
                        <option value="manual">Manual</option>
                      </select>

                      <button
                        onClick={() => updateConfigMutation.mutate({
                          configId: config.id,
                          data: { is_active: !config.is_active }
                        })}
                        style={{
                          padding: 'var(--space-2)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--glass-border)',
                          background: config.is_active ? 'var(--color-success-100)' : 'var(--bg-primary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)'
                        }}
                        title={config.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {config.is_active ? (
                          <Pause style={{ width: '16px', height: '16px', color: 'var(--color-success-600)' }} />
                        ) : (
                          <Play style={{ width: '16px', height: '16px', color: 'var(--text-secondary)' }} />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: 'var(--space-8)',
                color: 'var(--text-secondary)'
              }}>
                <AlertCircle style={{ width: '48px', height: '48px', margin: '0 auto var(--space-4)', opacity: 0.5 }} />
                <p>Nenhuma configuracao de distribuicao encontrada.</p>
                <p style={{ fontSize: 'var(--text-sm)' }}>Clique no botao acima para ativar o Round Robin.</p>
              </div>
            )}
          </div>

          {/* Ordem dos Membros */}
          <div style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-6)',
            boxShadow: 'var(--shadow-glass)'
          }}>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-4)' }}>
              Ordem de Distribuicao
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
              Arraste ou use as setas para alterar a ordem. Membros no topo recebem primeiro no Round Robin.
            </p>

            {teamMembers && teamMembers.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {[...teamMembers]
                  .sort((a, b) => a.distribution_order - b.distribution_order)
                  .map((member, index) => (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 'var(--space-3) var(--space-4)',
                        background: member.is_available_for_distribution ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid var(--glass-border)',
                        opacity: member.is_available_for_distribution ? 1 : 0.6
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <span style={{
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'var(--color-primary-100)',
                          color: 'var(--color-primary-700)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: '600'
                        }}>
                          {index + 1}
                        </span>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600))',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '600',
                          fontSize: 'var(--text-sm)'
                        }}>
                          {member.user_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                            {member.user_name}
                          </p>
                          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                            {member.user_email}
                          </p>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <button
                          onClick={() => toggleMemberAvailabilityMutation.mutate({
                            teamId: selectedTeamId,
                            memberId: member.id,
                            isAvailable: !member.is_available_for_distribution
                          })}
                          style={{
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-md)',
                            border: 'none',
                            background: member.is_available_for_distribution ? 'var(--color-success-100)' : 'var(--color-gray-100)',
                            color: member.is_available_for_distribution ? 'var(--color-success-700)' : 'var(--color-gray-600)',
                            cursor: 'pointer',
                            fontSize: 'var(--text-xs)',
                            fontWeight: '500'
                          }}
                        >
                          {member.is_available_for_distribution ? 'Disponivel' : 'Indisponivel'}
                        </button>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <button
                            onClick={() => moveMember(member.id, 'up')}
                            disabled={index === 0}
                            style={{
                              padding: '2px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--glass-border)',
                              background: 'var(--bg-primary)',
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              opacity: index === 0 ? 0.5 : 1
                            }}
                          >
                            <ChevronUp style={{ width: '14px', height: '14px' }} />
                          </button>
                          <button
                            onClick={() => moveMember(member.id, 'down')}
                            disabled={index === teamMembers.length - 1}
                            style={{
                              padding: '2px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--glass-border)',
                              background: 'var(--bg-primary)',
                              cursor: index === teamMembers.length - 1 ? 'not-allowed' : 'pointer',
                              opacity: index === teamMembers.length - 1 ? 0.5 : 1
                            }}
                          >
                            <ChevronDown style={{ width: '14px', height: '14px' }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 'var(--space-4)' }}>
                Nenhum membro na equipe.
              </p>
            )}
          </div>

          {/* Distribuição por Usuário */}
          {distributionStats && distributionStats.by_user.length > 0 && (
            <div style={{
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--glass-border)',
              padding: 'var(--space-6)',
              boxShadow: 'var(--shadow-glass)'
            }}>
              <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-4)' }}>
                Distribuicoes por Usuario (Ultimos 7 dias)
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {distributionStats.by_user.map((userStat) => {
                  const maxCount = Math.max(...distributionStats.by_user.map(u => u.count));
                  const percentage = maxCount > 0 ? (userStat.count / maxCount) * 100 : 0;

                  return (
                    <div key={userStat.user_id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                          {userStat.user_name}
                        </span>
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: '600', color: 'var(--text-primary)' }}>
                          {userStat.count}
                        </span>
                      </div>
                      <div style={{
                        height: '8px',
                        background: 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-full)',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${percentage}%`,
                          background: 'linear-gradient(90deg, var(--color-primary-500), var(--color-primary-600))',
                          borderRadius: 'var(--radius-full)',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
